<template>
    <div class="chat-widget" v-if="isAuthenticated && canUseChat">
        <!-- Chat bubble button -->
        <button
            class="chat-bubble"
            :class="{ 'has-unread': totalUnread > 0 }"
            @click="toggleWidget"
            :title="isOpen ? 'Fermer le chat' : 'Ouvrir le chat'"
        >
            <i class="material-icons">{{ isOpen ? 'close' : 'chat' }}</i>
            <ChatUnreadBadge
                v-if="totalUnread > 0 && !isOpen"
                :count="totalUnread"
                class="bubble-badge"
            />
        </button>

        <!-- Chat window -->
        <transition name="slide-up">
            <div v-if="isOpen" class="widget-window">
                <ChatWindow
                    :conversations="conversations"
                    :active-conversation="activeConversation"
                    :messages="messages"
                    :current-user-id="currentUserId"
                    :user-type="userType"
                    :online-users="onlineUsers"
                    :typing-users="typingUsers"
                    :total-unread="totalUnread"
                    :loading-conversations="loadingConversations"
                    :loading-messages="loadingMessages"
                    :has-more-messages="hasMoreMessages"
                    :show-close-button="true"
                    :compact-mode="true"
                    :max-file-size="maxFileSize"
                    :allowed-file-types="allowedFileTypes"
                    @select-conversation="selectConversation"
                    @clear-active="clearActiveConversation"
                    @send-message="sendMessage"
                    @typing-start="startTyping"
                    @typing-stop="stopTyping"
                    @load-more-messages="loadMoreMessages"
                    @file-select="handleFileUpload"
                    @close="closeWidget"
                    @new-conversation="showNewConversationModal"
                />
            </div>
        </transition>

        <!-- Overlay for mobile -->
        <div
            v-if="isOpen && isMobile"
            class="widget-overlay"
            @click="closeWidget"
        ></div>

        <!-- Seller Selection Modal -->
        <div v-if="showSellerModal" class="seller-modal-overlay" @click.self="showSellerModal = false">
            <div class="seller-modal">
                <div class="seller-modal-header">
                    <h3>Nouvelle conversation</h3>
                    <button class="close-btn" @click="showSellerModal = false">
                        <i class="material-icons">close</i>
                    </button>
                </div>
                <div class="seller-modal-body">
                    <div class="search-box">
                        <i class="material-icons">search</i>
                        <input
                            type="text"
                            v-model="sellerSearch"
                            placeholder="Rechercher un vendeur..."
                            @input="searchSellers"
                        />
                    </div>

                    <div v-if="loadingSellers" class="modal-loading">
                        <div class="spinner"></div>
                    </div>

                    <div v-else-if="availableSellers.length === 0" class="modal-empty">
                        <p>Aucun vendeur trouvé</p>
                    </div>

                    <div v-else class="sellers-list">
                        <div
                            v-for="seller in availableSellers"
                            :key="seller._id"
                            class="seller-item"
                            @click="startNewConversation(seller)"
                        >
                            <div class="seller-avatar">
                                <span>{{ getSellerInitial(seller) }}</span>
                            </div>
                            <div class="seller-info">
                                <span class="seller-name">{{ seller.fullName || seller.email }}</span>
                                <span class="seller-country">{{ seller.country }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import ChatWindow from './ChatWindow.vue';
import ChatUnreadBadge from './ChatUnreadBadge.vue';
import { socketService } from '@/plugins/socket';

export default {
    name: 'ChatWidget',
    components: {
        ChatWindow,
        ChatUnreadBadge
    },
    data() {
        return {
            isMobile: false,
            showSellerModal: false,
            sellerSearch: '',
            availableSellers: [],
            loadingSellers: false,
            searchTimeout: null
        };
    },
    computed: {
        ...mapState('chat', [
            'conversations',
            'activeConversation',
            'messages',
            'typingUsers',
            'onlineUsers',
            'widgetOpen',
            'loading',
            'loadingMessages'
        ]),
        ...mapGetters('chat', ['totalUnread']),
        isOpen() {
            return this.widgetOpen;
        },
        loadingConversations() {
            return this.loading;
        },
        hasMoreMessages() {
            if (!this.activeConversation) return false;
            var hasMore = this.$store.state.chat.hasMoreMessages;
            return (hasMore && hasMore[this.activeConversation._id]) || false;
        },
        currentUserId() {
            const user = this.$store.state.user;
            return (user && user._id) || '';
        },
        userType() {
            const user = this.$store.state.user;
            return (user && user.type) || 'Seller';
        },
        isAuthenticated() {
            return !!this.currentUserId;
        },
        canUseChat() {
            // Only Sellers and Account Managers can use chat
            return ['Seller', 'AccountManager'].includes(this.userType);
        },
        maxFileSize() {
            const settings = this.$store.state.settingsGlobal;
            return (settings && settings.chat && settings.chat.maxFileSize) || 10485760;
        },
        allowedFileTypes() {
            const settings = this.$store.state.settingsGlobal;
            return (settings && settings.chat && settings.chat.allowedFileTypes) ||
                   ['pdf', 'jpg', 'jpeg', 'png', 'doc', 'docx', 'xls', 'xlsx'];
        }
    },
    watch: {
        isAuthenticated: {
            immediate: true,
            handler(authenticated) {
                if (authenticated && this.canUseChat) {
                    this.initializeChat();
                }
            }
        },
        activeConversation(conversation) {
            if (conversation) {
                this.joinConversation(conversation._id);
                this.markAsRead(conversation._id);
            }
        }
    },
    mounted() {
        this.checkMobile();
        window.addEventListener('resize', this.checkMobile);

        // Handle keyboard shortcuts
        document.addEventListener('keydown', this.handleKeydown);
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.checkMobile);
        document.removeEventListener('keydown', this.handleKeydown);

        // Leave active conversation
        if (this.activeConversation) {
            this.leaveConversation(this.activeConversation._id);
        }
    },
    methods: {
        ...mapActions('chat', [
            'initSocket',
            'setActiveConversation',
            'toggleWidget',
            'openWidget',
            'closeWidget'
        ]),
        checkMobile() {
            this.isMobile = window.innerWidth < 768;
        },
        handleKeydown(event) {
            // Escape to close
            if (event.key === 'Escape' && this.isOpen) {
                this.closeWidget();
            }
        },
        async initializeChat() {
            try {
                // Initialize socket connection
                this.initSocket();

                // Fetch conversations - use direct API call since store action expects server.apiCall
                await this.loadConversations();
            } catch (error) {
                console.error('Failed to initialize chat:', error);
            }
        },
        async loadConversations() {
            this.$store.commit('chat/SET_LOADING', true);
            try {
                var response = await this.$server.getMyConversations('conversations', { limit: 100 });
                if (response && response.content) {
                    var results = response.content.results || response.content;
                    if (Array.isArray(results)) {
                        this.$store.commit('chat/SET_CONVERSATIONS', results);
                        var self = this;
                        results.forEach(function(conv) {
                            var unread = conv.unreadBySeller || conv.unreadByAM || 0;
                            self.$store.commit('chat/SET_UNREAD_COUNT', { conversationId: conv._id, count: unread });
                        });
                    }
                }
            } catch (error) {
                console.error('Failed to load conversations:', error);
            } finally {
                this.$store.commit('chat/SET_LOADING', false);
            }
        },
        async selectConversation(conversation) {
            // Leave previous conversation room
            if (this.activeConversation && this.activeConversation._id !== conversation._id) {
                this.leaveConversation(this.activeConversation._id);
            }

            // Set active and fetch messages
            this.setActiveConversation(conversation);

            // Always load fresh messages when selecting a conversation
            await this.loadMessages(conversation._id);
        },
        async loadMessages(conversationId, page) {
            page = page || 1;
            this.$store.commit('chat/SET_LOADING_MESSAGES', true);
            try {
                var response = await this.$server.getMessages('messages', {
                    conversationId: conversationId,
                    page: page,
                    limit: 50
                });
                if (response && response.content && response.content.results) {
                    var append = page > 1;
                    this.$store.commit('chat/SET_MESSAGES', {
                        conversationId: conversationId,
                        messages: response.content.results,
                        append: append
                    });
                    var hasMore = response.content.results.length >= 50;
                    this.$store.commit('chat/SET_HAS_MORE_MESSAGES', { conversationId: conversationId, hasMore: hasMore });
                }
            } catch (error) {
                console.error('Failed to load messages:', error);
            } finally {
                this.$store.commit('chat/SET_LOADING_MESSAGES', false);
            }
        },
        clearActiveConversation() {
            if (this.activeConversation) {
                this.leaveConversation(this.activeConversation._id);
            }
            this.$store.commit('chat/SET_ACTIVE_CONVERSATION', null);
        },
        joinConversation(conversationId) {
            socketService.joinConversation(conversationId);
        },
        leaveConversation(conversationId) {
            socketService.leaveConversation(conversationId);
        },
        async sendMessage(data) {
            var conversationId = data.conversationId;
            var content = data.content;
            var type = data.type;
            var attachment = data.attachment;

            // Generate temp ID for optimistic update
            var tempId = 'temp_' + Date.now();

            // Add optimistic message to store
            var tempMessage = {
                _id: tempId,
                tempId: tempId,
                conversation: conversationId,
                content: content,
                type: type || 'text',
                attachment: attachment,
                sender: { _id: this.currentUserId },
                status: 'sending',
                createdAt: new Date().toISOString()
            };
            this.$store.commit('chat/ADD_MESSAGE', { conversationId: conversationId, message: tempMessage });

            // Try to send via socket, fall back to REST API if socket not connected
            var sent = socketService.sendMessage(conversationId, content, type, attachment, tempId);
            if (!sent) {
                console.log('[ChatWidget] Socket send failed, using REST API fallback');
                try {
                    var response = await this.$server.sendMessage('messages', {
                        conversationId: conversationId,
                        content: content,
                        type: type || 'text',
                        attachment: attachment
                    });
                    if (response && response.content) {
                        var msg = response.content;
                        // Update the temp message with the real one
                        this.$store.commit('chat/ADD_MESSAGE', {
                            conversationId: conversationId,
                            message: { ...msg, tempId: tempId }
                        });
                        // Also update conversation's lastMessage
                        this.$store.commit('chat/UPDATE_CONVERSATION', {
                            conversationId: conversationId,
                            updates: {
                                lastMessage: {
                                    content: msg.content,
                                    sender: msg.sender,
                                    sentAt: msg.createdAt,
                                    type: msg.type
                                },
                                updatedAt: msg.createdAt
                            }
                        });
                    }
                } catch (error) {
                    console.error('[ChatWidget] REST API send failed:', error);
                    // Mark message as failed
                    tempMessage.status = 'failed';
                    this.$store.commit('chat/ADD_MESSAGE', { conversationId: conversationId, message: tempMessage });
                }
            }
        },
        startTyping(conversationId) {
            socketService.startTyping(conversationId);
        },
        stopTyping(conversationId) {
            socketService.stopTyping(conversationId);
        },
        markAsRead(conversationId) {
            socketService.markAsRead(conversationId);
            this.$store.commit('chat/CLEAR_UNREAD', conversationId);
        },
        loadMoreMessages() {
            if (this.activeConversation && !this.loadingMessages && this.hasMoreMessages) {
                var currentMessages = this.messages[this.activeConversation._id] || [];
                var page = Math.ceil(currentMessages.length / 50) + 1;
                this.loadMessages(this.activeConversation._id, page);
            }
        },
        async handleFileUpload({ conversationId, file }) {
            try {
                // Create form data
                const formData = new FormData();
                formData.append('file', file);
                formData.append('conversationId', conversationId);

                // Upload file via API
                const response = await this.$server.uploadFile('messages', formData);

                if (response && response.content) {
                    // File uploaded, send message with attachment
                    const type = file.type.startsWith('image/') ? 'image' : 'file';

                    this.sendMessage({
                        conversationId,
                        content: file.name,
                        type,
                        attachment: response.content
                    });
                }
            } catch (error) {
                console.error('File upload failed:', error);
                this.$toast.error('Échec du téléchargement du fichier');
            }
        },
        showNewConversationModal() {
            this.showSellerModal = true;
            this.sellerSearch = '';
            this.loadAvailableSellers();
        },
        async loadAvailableSellers() {
            this.loadingSellers = true;
            try {
                var response = await this.$server.getAssignedSellers('accountManager', { limit: 20 });
                if (response && response.content) {
                    this.availableSellers = response.content.results || [];
                }
            } catch (error) {
                console.error('Failed to load sellers:', error);
                this.availableSellers = [];
            } finally {
                this.loadingSellers = false;
            }
        },
        searchSellers() {
            var self = this;
            if (this.searchTimeout) {
                clearTimeout(this.searchTimeout);
            }
            this.searchTimeout = setTimeout(function() {
                self.doSearchSellers();
            }, 300);
        },
        async doSearchSellers() {
            if (!this.sellerSearch.trim()) {
                this.loadAvailableSellers();
                return;
            }
            this.loadingSellers = true;
            try {
                var response = await this.$server.getAssignedSellers('accountManager', {
                    search: this.sellerSearch,
                    limit: 20
                });
                if (response && response.content) {
                    this.availableSellers = response.content.results || [];
                }
            } catch (error) {
                console.error('Failed to search sellers:', error);
            } finally {
                this.loadingSellers = false;
            }
        },
        getSellerInitial(seller) {
            if (!seller) return '';
            var name = seller.fullName || seller.firstName || seller.email || '';
            return name.charAt(0).toUpperCase();
        },
        async startNewConversation(seller) {
            try {
                var response = await this.$server.getOrCreateConversation('conversations', {
                    sellerId: seller._id
                });
                if (response && response.content) {
                    this.showSellerModal = false;
                    this.$store.commit('chat/ADD_CONVERSATION', response.content);
                    await this.selectConversation(response.content);
                }
            } catch (error) {
                console.error('Failed to create conversation:', error);
                if (this.$toast) {
                    this.$toast.error('Erreur lors de la création de la conversation');
                }
            }
        }
    }
};
</script>

<style scoped>
.chat-widget {
    position: fixed;
    bottom: 24px;
    right: 24px;
    z-index: 9999;
}

.chat-bubble {
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background: linear-gradient(135deg, #059669 0%, #10b981 100%);
    color: white;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 16px rgba(5, 150, 105, 0.4);
    transition: all 0.3s ease;
    position: relative;
}

.chat-bubble:hover {
    transform: scale(1.1);
    box-shadow: 0 6px 20px rgba(5, 150, 105, 0.5);
}

.chat-bubble.has-unread {
    animation: pulse 2s infinite;
}

@keyframes pulse {
    0% {
        box-shadow: 0 4px 16px rgba(5, 150, 105, 0.4);
    }
    50% {
        box-shadow: 0 4px 24px rgba(5, 150, 105, 0.6);
    }
    100% {
        box-shadow: 0 4px 16px rgba(5, 150, 105, 0.4);
    }
}

.chat-bubble i {
    font-size: 24px;
}

.bubble-badge {
    position: absolute;
    top: -4px;
    right: -4px;
}

.widget-window {
    position: absolute;
    bottom: 70px;
    right: 0;
    width: 380px;
    height: 550px;
    max-height: calc(100vh - 120px);
    background-color: #ffffff;
    border-radius: 16px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
    overflow: hidden;
}

.widget-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: -1;
}

/* Slide up animation */
.slide-up-enter-active,
.slide-up-leave-active {
    transition: all 0.3s ease;
}

.slide-up-enter,
.slide-up-leave-to {
    opacity: 0;
    transform: translateY(20px);
}

/* Mobile styles */
@media (max-width: 768px) {
    .chat-widget {
        bottom: 16px;
        right: 16px;
    }

    .widget-window {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        width: 100%;
        height: 100%;
        max-height: none;
        border-radius: 0;
    }
}

/* Tablet adjustments */
@media (min-width: 768px) and (max-width: 1024px) {
    .widget-window {
        width: 360px;
        height: 500px;
    }
}

/* Seller Modal Styles */
.seller-modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10001;
}

.seller-modal {
    background: #ffffff;
    border-radius: 16px;
    width: 90%;
    max-width: 400px;
    max-height: 70vh;
    overflow: hidden;
    display: flex;
    flex-direction: column;
}

.seller-modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    border-bottom: 1px solid #e5e7eb;
}

.seller-modal-header h3 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: #111827;
}

.seller-modal-header .close-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border: none;
    background: none;
    border-radius: 50%;
    cursor: pointer;
    color: #6b7280;
    transition: all 0.2s;
}

.seller-modal-header .close-btn:hover {
    background-color: #f3f4f6;
    color: #111827;
}

.seller-modal-body {
    padding: 16px;
    overflow-y: auto;
    flex: 1;
}

.search-box {
    position: relative;
    margin-bottom: 16px;
}

.search-box i {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #9ca3af;
    font-size: 20px;
}

.search-box input {
    width: 100%;
    padding: 10px 12px 10px 40px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    font-size: 14px;
    outline: none;
    transition: border-color 0.2s;
}

.search-box input:focus {
    border-color: #059669;
}

.modal-loading {
    display: flex;
    justify-content: center;
    padding: 32px;
}

.modal-loading .spinner {
    width: 32px;
    height: 32px;
    border: 3px solid #e5e7eb;
    border-top-color: #059669;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
}

@keyframes spin {
    to { transform: rotate(360deg); }
}

.modal-empty {
    text-align: center;
    padding: 32px;
    color: #9ca3af;
}

.sellers-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.seller-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 12px;
    border-radius: 8px;
    cursor: pointer;
    transition: background-color 0.2s;
}

.seller-item:hover {
    background-color: #f3f4f6;
}

.seller-avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: linear-gradient(135deg, #059669 0%, #10b981 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-weight: 600;
    font-size: 14px;
    flex-shrink: 0;
}

.seller-info {
    display: flex;
    flex-direction: column;
    min-width: 0;
}

.seller-name {
    font-weight: 500;
    color: #111827;
    font-size: 14px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.seller-country {
    font-size: 12px;
    color: #6b7280;
}
</style>
